import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ShieldCheck, Package } from 'lucide-react';
import { OpNoteEvidencePayload, OpNoteImplant } from '../opNoteResponseTypes';

interface EvidencePanelProps {
  evidencePayloads: OpNoteEvidencePayload[];
  implants: OpNoteImplant[];
}

export function EvidencePanel({ evidencePayloads, implants }: EvidencePanelProps) {
  const hasEvidence = evidencePayloads.length > 0;
  const hasImplants = implants.length > 0;

  if (!hasEvidence && !hasImplants) return null;

  return (
    <Card className="border-border/60">
      <CardContent className="p-5 space-y-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Audit Defense & Implants
        </h4>

        {hasEvidence && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase">Evidence Justifications</p>
            {evidencePayloads.map((ep, i) => (
              <div key={i} className="rounded-lg border bg-muted/30 p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px] font-mono h-4">{ep.cpt_code}</Badge>
                  {ep.documentation_reference && (
                    <span className="text-[10px] text-muted-foreground">Ref: {ep.documentation_reference}</span>
                  )}
                </div>
                <p className="text-xs text-foreground leading-relaxed">{ep.justification}</p>
              </div>
            ))}
          </div>
        )}

        {hasEvidence && hasImplants && <Separator />}

        {hasImplants && (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase flex items-center gap-1.5">
              <Package className="w-3 h-3" /> Implants & Biologics
            </p>
            <div className="space-y-1.5">
              {implants.map((imp, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{imp.name}</span>
                    {imp.manufacturer && (
                      <span className="text-[10px] text-muted-foreground">({imp.manufacturer})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Qty: {imp.quantity}</span>
                    <span className="font-mono">{imp.catalog_id}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
